import { useEffect, useMemo, useState } from "react";
import { Download, Trash2, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import LeadUploader from "@/components/LeadUploader";
import LeadCard from "@/components/LeadCard";
import UpgradeBanner from "@/components/UpgradeBanner";
import UpgradeDialog from "@/components/UpgradeDialog";
import TrialBanner from "@/components/TrialBanner";
import UsageMeter from "@/components/UsageMeter";
import CreditPacksDialog from "@/components/CreditPacksDialog";
import ProfileSetupBanner from "@/components/ProfileSetupBanner";
import LeadFilterBar from "@/components/LeadFilterBar";
import Pagination from "@/components/Pagination";
import { Button } from "@/components/ui/button";
import { leadsToCsv, downloadCsv } from "@/lib/csv";

const PAGE_SIZE = 10;

const EMPTY_GRAPHIC =
  "https://images.unsplash.com/photo-1545987796-200677ee1011?auto=format&fit=crop&w=600&q=80";

export default function Dashboard() {
  const { refresh } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [enrichCount, setEnrichCount] = useState(0);
  const [reenrichingId, setReenrichingId] = useState(null);

  // filter / search / pagination state
  const [query, setQuery] = useState("");
  const [scoreFilter, setScoreFilter] = useState("all");
  const [emailFilter, setEmailFilter] = useState("all");
  const [page, setPage] = useState(1);

  // periodic upgrade nudge after every 5 enrichments
  useEffect(() => {
    if (enrichCount > 0 && enrichCount % 5 === 0) {
      toast("✨ Want 5× richer signals?", {
        description: "Upgrade to Deep Enrichment for hiring-board scraping & multi-page crawls.",
        action: { label: "See plans", onClick: () => setUpgradeOpen(true) },
        duration: 8000,
      });
    }
  }, [enrichCount]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/leads");
      setLeads(data.leads || []);
    } catch (e) {
      toast.error("Could not load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const enrichLeads = async (rows) => {
    setBusy(true);
    setProgress({ current: 0, total: rows.length });
    let added = [];
    let skippedDupes = 0;

    if (rows.length >= 2) {
      // Batched path — bulk-match optimisation kicks in on the backend.
      const CHUNK = 10;
      for (let start = 0; start < rows.length; start += CHUNK) {
        const chunk = rows.slice(start, start + CHUNK);
        try {
          const { data } = await http.post("/leads/enrich-batch", { leads: chunk });
          skippedDupes += data.skipped_count || 0;
          for (const r of data.results || []) {
            if (!r) continue;
            if (r.error) {
              toast.error(`Failed: ${r.input?.company || "lead"} — ${r.error}`);
            } else {
              added.push(r);
              setLeads((prev) => [r, ...prev]);
            }
          }
        } catch (e) {
          toast.error(`Batch failed: ${e.response?.data?.detail || e.message}`);
        }
        setProgress({ current: Math.min(start + CHUNK, rows.length), total: rows.length });
      }
    } else {
      // Single-lead path — keep the snappier per-lead UX.
      for (let i = 0; i < rows.length; i++) {
        const lead = rows[i];
        try {
          const { data } = await http.post("/leads/enrich", lead);
          if (data._dedupe === "existing") {
            skippedDupes += 1;
            toast.info(data._message || `"${lead.email || lead.company}" already in your dashboard — opened existing record.`);
          }
          added.push(data);
          setLeads((prev) => [data, ...prev.filter((l) => l.id !== data.id)]);
        } catch (e) {
          toast.error(`Failed to enrich ${lead.company}: ${e.response?.data?.detail || e.message}`);
        }
        setProgress({ current: i + 1, total: rows.length });
      }
    }

    setBusy(false);
    if (added.length) {
      const skipMsg = skippedDupes > 0 ? ` · ${skippedDupes} duplicate contact${skippedDupes === 1 ? "" : "s"} skipped` : "";
      toast.success(`Enriched ${added.length} lead${added.length === 1 ? "" : "s"}${skipMsg}.`);
      setEnrichCount((n) => n + added.length);
    } else if (skippedDupes > 0) {
      toast.info(`${skippedDupes} duplicate contact${skippedDupes === 1 ? "" : "s"} skipped — already in your dashboard.`);
    }
    // Refresh user (to update usage meter) — non-blocking
    refresh?.();
  };

  const onDelete = async (id) => {
    try {
      await http.delete(`/leads/${id}`);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Lead removed");
      refresh?.();
    } catch {
      toast.error("Could not remove lead");
    }
  };

  const onClearAll = async () => {
    if (!leads.length) return;
    if (!window.confirm("Delete all enriched leads? This cannot be undone.")) return;
    try {
      await http.delete("/leads");
      setLeads([]);
      toast.success("All leads cleared");
      refresh?.();
    } catch {
      toast.error("Could not clear leads");
    }
  };

  const onReenrich = async (id) => {
    setReenrichingId(id);
    try {
      const { data } = await http.post(`/leads/${id}/re-enrich`);
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
      toast.success("Lead re-enriched · 1 credit used");
      refresh?.();
    } catch (e) {
      toast.error(`Re-enrich failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setReenrichingId(null);
    }
  };

  const onFindEmail = async (id) => {
    try {
      const { data } = await http.post(`/leads/${id}/find-email`);
      if (data?.found && data?.lead) {
        setLeads((prev) => prev.map((l) => (l.id === id ? data.lead : l)));
        toast.success(data.message || "Found a better email · 1 credit used");
        refresh?.();
        return;
      }
      if (data?.state === "pending" && data?.lookup_id) {
        // Primary provider missed — LeadIQ is searching in the background.
        toast.info(data.message || "Searching backup source (30–90s)...");
        const lookupId = data.lookup_id;
        // Poll every 10s for up to 3 minutes (stays under provider rate cap)
        const maxAttempts = 18;
        for (let i = 0; i < maxAttempts; i++) {
          await new Promise((r) => setTimeout(r, 10000));
          try {
            const { data: poll } = await http.get(`/email-lookups/${lookupId}`);
            if (poll?.state === "settled") {
              if (poll.lead) {
                setLeads((prev) => prev.map((l) => (l.id === id ? poll.lead : l)));
              }
              toast.success(`Backup found: ${poll.email} · 1 credit used`);
              refresh?.();
              return;
            }
            if (poll?.state === "refunded") {
              toast.info("No email found by backup source · credit refunded");
              return;
            }
            if (poll?.state === "error") {
              toast.error(`Backup lookup failed: ${poll.reason || "unknown"}`);
              return;
            }
          } catch {
            // transient; keep polling
          }
        }
        toast.info("Backup lookup is still running — check back in a minute.");
        return;
      }
      // Miss — credit refunded, no usage event logged.
      toast.info(data?.message || "No better email found · credit refunded");
    } catch (e) {
      toast.error(`Email lookup failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const onRevealFunding = async (id, { silent = false } = {}) => {
    try {
      const { data } = await http.post(`/leads/${id}/reveal-funding`);
      if (data?.found && data?.lead) {
        setLeads((prev) => prev.map((l) => (l.id === id ? data.lead : l)));
        if (!silent) {
          toast.success(data.message || (data.included_in_plan
            ? "Funding intel loaded · included in your plan"
            : "Funding intel loaded · 1 credit used"));
        }
        refresh?.();
      } else if (!silent) {
        toast.info(data?.message || "No funding intel found");
      }
    } catch (e) {
      if (!silent) toast.error(`Funding lookup failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const onExport = () => {
    if (!leads.length) return;
    const csv = leadsToCsv(leads);
    downloadCsv(`leadiq-export-${new Date().toISOString().slice(0, 10)}.csv`, csv);
    toast.success("CSV downloaded");
  };

  const stats = useMemo(() => {
    const high = leads.filter((l) => (l.enrichment?.opportunity_score ?? 0) >= 70).length;
    const med = leads.filter((l) => {
      const s = l.enrichment?.opportunity_score ?? 0;
      return s >= 40 && s < 70;
    }).length;
    const low = leads.filter((l) => (l.enrichment?.opportunity_score ?? 0) < 40).length;
    const avg = leads.length
      ? Math.round(leads.reduce((a, l) => a + (l.enrichment?.opportunity_score ?? 0), 0) / leads.length)
      : 0;
    return { high, med, low, avg };
  }, [leads]);

  // ---- search + filter (client-side) ----
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads.filter((l) => {
      // text search across input + contact fields
      if (q) {
        const i = l.input || {};
        const c = l.enrichment?.contact || {};
        const hay = [
          i.company,
          i.domain,
          i.name,
          i.email,
          i.title,
          c.email,
          c.title,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      // score tier filter
      if (scoreFilter !== "all") {
        const s = l.enrichment?.opportunity_score ?? 0;
        if (scoreFilter === "high" && s < 70) return false;
        if (scoreFilter === "med" && (s < 40 || s >= 70)) return false;
        if (scoreFilter === "low" && s >= 40) return false;
      }
      // email status filter
      if (emailFilter !== "all") {
        const c = l.enrichment?.contact || {};
        const i = l.input || {};
        const hasEmail = !!(c.email || i.email);
        const apolloEmail = !!c.email;
        const status = hasEmail
          ? apolloEmail
            ? c.email_status || "unverified"
            : "unverified"
          : "unavailable";
        if (status !== emailFilter) return false;
      }
      return true;
    });
  }, [leads, query, scoreFilter, emailFilter]);

  // reset page when filters change or list shrinks below current page
  useEffect(() => {
    setPage(1);
  }, [query, scoreFilter, emailFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageLeads = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* hero */}
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-2">
          Workspace · Enrichment
        </div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] font-extrabold text-ink-900">
          Lead intelligence, <span className="font-light">at scale.</span>
        </h1>
        <p className="text-ink-500 text-sm mt-3 max-w-2xl leading-relaxed">
          Upload a CSV or enter a single contact. Each lead is enriched with live web research:
          tech stack, hiring signals, news, buying committee and a tailored engagement playbook.
        </p>
      </div>

      <TrialBanner onUpgrade={() => setUpgradeOpen(true)} />
      <ProfileSetupBanner />
      <UsageMeter
        onUpgrade={() => setUpgradeOpen(true)}
        onBuyCredits={() => setCreditsOpen(true)}
      />
      <UpgradeBanner onClick={() => setUpgradeOpen(true)} />
      <UpgradeDialog open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
      <CreditPacksDialog open={creditsOpen} onClose={() => setCreditsOpen(false)} />

      {/* stats grid */}
      {leads.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[
            { label: "Total leads", value: leads.length, mono: true },
            { label: "High priority", value: stats.high, color: "text-[#2E7D32]" },
            { label: "Medium", value: stats.med, color: "text-[#F57F17]" },
            { label: "Avg score", value: stats.avg, mono: true },
          ].map((s, i) => (
            <div key={i} className="bg-white border border-ink-100 rounded-sm p-4">
              <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
                {s.label}
              </div>
              <div className={`font-display text-4xl font-black tracking-tighter mt-2 ${s.color || "text-ink-900"}`}>
                {s.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* uploader */}
      <LeadUploader onEnrich={enrichLeads} busy={busy} progress={progress} />

      {/* divider */}
      <div className="my-10 flex items-center gap-4">
        <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
          Enriched leads · {leads.length}
        </div>
        <div className="flex-1 h-px bg-ink-100" />
        <div className="flex items-center gap-2">
          <Button
            data-testid="export-csv-button"
            onClick={onExport}
            disabled={!leads.length}
            variant="outline"
            size="sm"
            className="rounded-sm border-ink-200 hover:bg-ink-50"
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </Button>
          <Button
            data-testid="clear-all-button"
            onClick={onClearAll}
            disabled={!leads.length}
            variant="ghost"
            size="sm"
            className="rounded-sm text-ink-500 hover:text-accent"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Clear all
          </Button>
        </div>
      </div>

      {/* leads list */}
      {loading ? (
        <div className="text-sm text-ink-400 py-12 text-center">Loading…</div>
      ) : leads.length === 0 ? (
        <div data-testid="empty-state" className="bg-white border border-dashed border-ink-200 rounded-sm py-16 px-8 text-center">
          <img src={EMPTY_GRAPHIC} alt="" className="w-40 h-40 object-contain mx-auto opacity-90 mb-6" />
          <BarChart3 className="w-5 h-5 text-ink-300 mx-auto mb-3" />
          <h3 className="font-display font-extrabold text-2xl text-ink-900 tracking-tight">
            No leads enriched yet
          </h3>
          <p className="text-sm text-ink-500 mt-2 max-w-md mx-auto">
            Upload a CSV with at least <span className="font-mono text-ink-700">company</span> and{" "}
            <span className="font-mono text-ink-700">domain</span> columns, or add a single lead above.
          </p>
        </div>
      ) : (
        <>
          <LeadFilterBar
            query={query}
            onQueryChange={setQuery}
            scoreFilter={scoreFilter}
            onScoreChange={setScoreFilter}
            emailFilter={emailFilter}
            onEmailChange={setEmailFilter}
            total={leads.length}
            filtered={filtered.length}
          />

          {filtered.length === 0 ? (
            <div
              data-testid="no-filter-results"
              className="bg-white border border-dashed border-ink-200 rounded-sm py-12 px-8 text-center"
            >
              <h3 className="font-display font-extrabold text-lg text-ink-900 tracking-tight">
                No leads match your filters.
              </h3>
              <p className="text-sm text-ink-500 mt-2">
                Try clearing the search or adjusting the filters above.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-3" data-testid="leads-list">
                {pageLeads.map((lead, idx) => (
                  <LeadCard
                    key={lead.id || idx}
                    lead={lead}
                    index={pageStart + idx}
                    onDelete={onDelete}
                    onReenrich={onReenrich}
                    onFindEmail={onFindEmail}
                    onRevealFunding={onRevealFunding}
                    reenriching={reenrichingId === lead.id}
                  />
                ))}
              </div>
              <Pagination
                page={safePage}
                totalPages={totalPages}
                onChange={setPage}
              />
            </>
          )}
        </>
      )}
    </main>
  );
}
